import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"

import { Button } from "@cms/components/data/Button"
import { ControlledSystemDialog } from "@cms/components/data/Dialog"
import { SystemMenu } from "@cms/components/data/Menu"
import { useSnackbar } from "@cms/providers/SnackbarProvider"
import api from "@cms/utils/api"
import { FIXED_SEARCH_PARAMS } from "@cms/utils/constants"
import { commonStorySchema } from "@cms/utils/forms/story"
import { generateSearchParam } from "@cms/utils/helpers"
import { Routes, StoriesPath } from "@cms/utils/routes"
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks"
import { showTopBarMessageAction } from "@cms/utils/store/slices/app.slice"
import { generateIdempotencyKey, ISystemStory } from "@dl/declarations"
import MenuItem from "@mui/material/MenuItem"
import { useMutation } from "@tanstack/react-query"
import { BsThreeDots } from "react-icons/bs"
import { useNavigate } from "react-router-dom"

const DeleteStoryMenu = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch();
    const { showSnackbar } = useSnackbar();
    const { story } = useAppSelector((state) => state.editor);

    const mutation = useMutation({
        mutationFn: async () => {
            const response = await api.delete<ISystemStory>(`/stories/${story!._id}`)
            return response.data
        },
        onSuccess: () => {
            dispatch(showTopBarMessageAction({
                ttl: 5_000,
                type: 'success',
                content: 'Story deleted successfully.',
                pageId: Routes.STORIES,
            }));

            navigate({
                pathname: Routes.STORIES,
                search: `?${FIXED_SEARCH_PARAMS.SHOULD_REFRESH}=true`
            })
        },
        onError: (error) => {
            showSnackbar('Failed to delete story. ' + (error as any)?.response?.data?.message || (error as Error).message || 'Please try again later.', 'error');
        },
    })

    const handleDeleteStory = async () => {
        mutation.mutate()
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: "Delete Story",
                maxWidth: 'sm'
            }}
            trigger={{
                component: (
                    <Button
                        type="button"
                        inputVariant="red_text_white_bg"
                        showBorders={false}
                    >
                        Delete Story
                    </Button>
                )
            }}
            content={{
                component: (
                    <p>Are you sure you want to delete this story? This cannot be undone.</p>
                )
            }}
            actionsProps={{
                submit: {
                    label: 'Delete Story',
                    onClick: handleDeleteStory,
                }
            }}
        />
    )
}

const CloneStoryMenu = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch();
    const { showSnackbar } = useSnackbar();
    const { story } = useAppSelector((state) => state.editor);

    const form = useForm<{
        headline: string
        idempotency_key: string
    }>({
        defaultValues: {
            headline: (story?.headline || '') + ' - Clone',
            idempotency_key: generateIdempotencyKey(),
        },
        resolver: yupResolver(commonStorySchema)
    })

    const mutation = useMutation({
        mutationFn: async ({
            headline,
            idempotency_key
        }: {
            headline: string
            idempotency_key: string
        }) => {
            const response = await api.put<ISystemStory>(`/stories/${story!._id}/clone`, {
                headline,
                idempotency_key,
            })
            return response.data
        },
        onSuccess: (clonedStory) => {
            dispatch(showTopBarMessageAction({
                ttl: 5_000,
                type: 'success',
                content: 'Story cloned successfully.',
                pageId: Routes.EDITOR,
            }));

            form.reset({
                headline: (clonedStory?.headline || '') + ' - Clone',
                idempotency_key: generateIdempotencyKey(),
            });

            navigate({
                search: `?${generateSearchParam(StoriesPath, "id", clonedStory._id)}`
            })
        },
        onError: (error) => {
            showSnackbar('Failed to clone story. ' + (error as any)?.response?.data?.message || (error as Error).message || 'Please try again later.', 'error');
        },
    })

    const handleCloneStory = async () => {
        const values = form.getValues()
        mutation.mutate(values)
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: "Clone Story",
                maxWidth: 'sm'
            }}
            trigger={{
                component: (
                    <Button
                        type="button"
                        inputVariant="none"
                        showBorders={false}
                    >
                        Clone Story
                    </Button>
                )
            }}
            content={{
                component: (
                    <>
                        <label className="block mb-2 font-medium">
                            Story Headline
                        </label>
                        <input
                            type="text"
                            className="w-full p-2 border border-gray-300 rounded"
                            {...form.register('headline')}
                        />
                        {form.formState.errors.headline && (
                            <p className="text-red-500 mt-1">
                                {form.formState.errors.headline.message}
                            </p>
                        )}
                    </>
                )
            }}
            actionsProps={{
                submit: {
                    label: 'Clone Story',
                    onClick: handleCloneStory,
                    isLoading: mutation.isPending,
                }
            }}
        />
    )
}

export const StoryOptionsMenu = () => {
    return (
        <SystemMenu
            trigger={{
                component: (
                    <Button
                        as='div'
                        shape="square"
                    >
                        <BsThreeDots size={24} />
                    </Button>
                )
            }}
            content={{
                component: (
                    <>
                        <MenuItem>
                            <DeleteStoryMenu />
                        </MenuItem>
                        <MenuItem>
                            <CloneStoryMenu />
                        </MenuItem>
                    </>
                ),
            }}
        />
    )
}